import React, { useState } from 'react';
import * as XLSX from 'xlsx';

// Define types for our component
interface CsvToExcelProps {}

// Helper function to parse a CSV line respecting quoted values
const parseCSVLine = (line: string, delimiter: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      // Handle escaped double quotes
      if (i + 1 < line.length && line[i + 1] === '"') {
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        // Toggle the quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      // End of field
      values.push(currentValue);
      currentValue = '';
    } else {
      // Regular character
      currentValue += char;
    }
  }
  
  // Add the last value
  values.push(currentValue);
  
  return values;
};

// Helper function to guess the data type of a value
const convertValueType = (value: string): any => {
  // Empty values
  if (value === '') {
    return null;
  }
  
  // Boolean values
  if (value.toLowerCase() === 'true') {
    return true;
  }
  if (value.toLowerCase() === 'false') {
    return false;
  }
  
  // Null value
  if (value.toLowerCase() === 'null' || value.toLowerCase() === 'n/a') {
    return null;
  }
  
  // Number values
  if (/^-?\d+$/.test(value)) {
    return parseInt(value, 10);
  }
  if (/^-?\d+\.\d+$/.test(value)) {
    return parseFloat(value);
  }
  
  // Date values (ISO format)
  if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(.\d{3})?Z?)?$/.test(value)) {
    return new Date(value);
  }
  
  // Default to string
  return value;
};

// CSV to worksheet conversion function
const csvToWorksheet = (
  csv: string, 
  options: {
    header: boolean;
    delimiter: string;
    dynamicTyping: boolean;
    trimValues: boolean;
    skipEmptyLines: boolean;
  }
): XLSX.WorkSheet => {
  try {
    // Split the CSV by lines
    const lines = csv.split(/\r?\n/);
    
    // Handle empty CSV
    if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === '')) {
      return XLSX.utils.aoa_to_sheet([['No data']]);
    }
    
    // Process header row
    let headers: string[] = [];
    let startRow = 0;
    const data: any[][] = [];
    
    if (options.header) {
      // Use the first row as headers
      const headerRow = lines[0];
      headers = headerRow
        .split(options.delimiter)
        .map(header => options.trimValues ? header.trim() : header);
      data.push([...headers]); // Add header row to data
      startRow = 1;
    }
    
    // Process rows
    for (let i = startRow; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines if configured to do so
      if (options.skipEmptyLines && line === '') {
        continue;
      }
      
      const values = parseCSVLine(line, options.delimiter);
      
      if (values.length > 0) {
        const rowData: any[] = [];
        
        for (let j = 0; j < values.length; j++) {
          let value = options.trimValues ? values[j].trim() : values[j];
          
          // Apply dynamic typing if enabled
          if (options.dynamicTyping) {
            value = convertValueType(value);
          }
          
          rowData.push(value);
        }
        
        data.push(rowData);
      }
    }
    
    // Create worksheet from data
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    
    // Apply styling to header row if headers exist
    if (options.header) {
      // Get the range of the worksheet
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
      
      // Create a style for header cells
      for(let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({r: 0, c: C});
        if (!worksheet[cellAddress]) continue;
        
        // Apply bold and fill style for headers
        worksheet[cellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "EFEFEF" } }
        };
      }
    }
    
    return worksheet;
  } catch (error) {
    throw error;
  }
};

// Main component to convert CSV to Excel
const CsvToExcel: React.FC<CsvToExcelProps> = () => {
  // State for the CSV input
  const [csvInput, setCsvInput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isExcelGenerated, setIsExcelGenerated] = useState<boolean>(false);
  // State for conversion success message
  const [successMessage, setSuccessMessage] = useState<string>('');
  
  // Conversion options
  const [hasHeader, setHasHeader] = useState<boolean>(true);
  const [delimiter, setDelimiter] = useState<string>(',');
  const [dynamicTyping, setDynamicTyping] = useState<boolean>(true);
  const [trimValues, setTrimValues] = useState<boolean>(true);
  const [skipEmptyLines, setSkipEmptyLines] = useState<boolean>(true);
  const [sheetName, setSheetName] = useState<string>('Sheet1');
  const [excelFormat, setExcelFormat] = useState<string>('xlsx');
  
  // Handle CSV input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCsvInput(e.target.value);
    setError('');
    setIsExcelGenerated(false);
    setSuccessMessage('');
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Update the file name based on the uploaded file
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop(); // Remove extension
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }
    
    // Read the file content
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setCsvInput(event.target.result as string);
        setError('');
        setIsExcelGenerated(false);
        setSuccessMessage('');
      }
    };
    reader.onerror = () => {
      setError('Error reading the file');
    };
    reader.readAsText(file);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Handle sheet name change
  const handleSheetNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSheetName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    const exampleCsv = `Name,Age,Email,IsActive,JoinDate,Score
John Doe,32,john@example.com,true,2021-03-15,85.5
Jane Smith,28,jane@example.com,true,2020-11-20,92.0
Bob Johnson,45,bob@example.com,false,2019-07-08,76.8
Alice Brown,33,alice@example.com,true,2022-01-10,88.2
Charlie Wilson,,charlie@example.com,false,2020-02-28,`;

    setCsvInput(exampleCsv);
    setError('');
    setIsExcelGenerated(false);
    setSuccessMessage('');
  };

  // Convert CSV to Excel
  const convertCsvToExcel = () => {
    setError('');
    setIsLoading(true);
    setIsExcelGenerated(false);
    setSuccessMessage('');
    
    if (!csvInput.trim()) {
      setError('Please enter CSV data');
      setIsLoading(false);
      return;
    }

    try {
      // Set up conversion options
      const options = {
        header: hasHeader,
        delimiter,
        dynamicTyping,
        trimValues,
        skipEmptyLines
      };
      
      // Convert CSV to worksheet
      const worksheet = csvToWorksheet(csvInput, options);
      
      // Create workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      
      // Generate file and trigger download
      XLSX.writeFile(workbook, `${fileName || 'data'}.${excelFormat}`);
      
      setIsExcelGenerated(true);
      setSuccessMessage(`Excel file "${fileName}.${excelFormat}" has been generated and downloaded!`);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting CSV to Excel: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setCsvInput('');
    setError('');
    setFileName('data');
    setSheetName('Sheet1');
    setIsExcelGenerated(false);
    setSuccessMessage('');
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">CSV to Excel Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-green-50 p-4 rounded-md border border-green-100">
        <p className="text-green-800">
          Convert CSV (Comma-Separated Values) data to Excel format. Upload a CSV file or paste your data below.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="csvInput" className="font-medium text-gray-700">
                CSV Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-green-600 hover:text-green-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setCsvInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-green-50 file:text-green-700
                  hover:file:bg-green-100"
              />
            </div>
            
            <textarea
              id="csvInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-green-500 focus:border-green-500"
              value={csvInput}
              onChange={handleInputChange}
              placeholder="Paste your CSV data here, e.g.,
Name,Age,Email
John Doe,30,john@example.com
Jane Smith,25,jane@example.com"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                  Output File Name
                </label>
                <input
                  type="text"
                  id="fileName"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  value={fileName}
                  onChange={handleFileNameChange}
                  placeholder="Enter file name (without extension)"
                />
              </div>
              
              <div>
                <label htmlFor="sheetName" className="block text-sm font-medium text-gray-700 mb-1">
                  Worksheet Name
                </label>
                <input
                  type="text"
                  id="sheetName"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  value={sheetName}
                  onChange={handleSheetNameChange}
                  placeholder="Enter worksheet name"
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="excelFormat" className="block text-sm font-medium text-gray-700 mb-1">
                Excel Format
              </label>
              <select
                id="excelFormat"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                value={excelFormat}
                onChange={(e) => setExcelFormat(e.target.value)}
              >
                <option value="xlsx">XLSX - Excel 2007+ Format (.xlsx)</option>
                <option value="xls">XLS - Excel 97-2004 Format (.xls)</option>
                <option value="ods">ODS - OpenDocument Spreadsheet (.ods)</option>
              </select>
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-green-600 hover:text-green-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasHeader"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasHeader" className="ml-2 block text-sm text-gray-700">
                    First row contains headers
                  </label>
                </div>
                
                <div>
                  <label htmlFor="delimiter" className="block text-sm font-medium text-gray-700 mb-1">
                    Delimiter
                  </label>
                  <select
                    id="delimiter"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="dynamicTyping"
                    checked={dynamicTyping}
                    onChange={(e) => setDynamicTyping(e.target.checked)}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="dynamicTyping" className="ml-2 block text-sm text-gray-700">
                    Convert values to proper types (numbers, booleans, dates)
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="trimValues"
                    checked={trimValues}
                    onChange={(e) => setTrimValues(e.target.checked)}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="trimValues" className="ml-2 block text-sm text-gray-700">
                    Trim whitespace from values
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="skipEmptyLines"
                    checked={skipEmptyLines}
                    onChange={(e) => setSkipEmptyLines(e.target.checked)}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="skipEmptyLines" className="ml-2 block text-sm text-gray-700">
                    Skip empty lines
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                onClick={convertCsvToExcel}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Excel'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Excel Output
            </label>
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isExcelGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    After conversion, Excel file will be downloaded automatically to your computer.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isExcelGenerated && (
              <div className="flex-grow flex flex-col">
                <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-md mb-4">
                  <div className="flex items-center mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="font-medium">Success!</p>
                  </div>
                  <p>{successMessage}</p>
                </div>
                
                <div className="bg-gray-50 border border-gray-200 p-4 rounded-md flex-grow">
                  <h3 className="font-medium text-gray-700 mb-2">Preview Information</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li><strong>File Name:</strong> {fileName}.{excelFormat}</li>
                    <li><strong>Worksheet Name:</strong> {sheetName}</li>
                    <li><strong>Format:</strong> {excelFormat.toUpperCase()}</li>
                    <li><strong>Headers Included:</strong> {hasHeader ? 'Yes' : 'No'}</li>
                    <li><strong>Delimiter Used:</strong> {delimiter === ',' ? 'Comma' : delimiter === ';' ? 'Semicolon' : delimiter === '\t' ? 'Tab' : 'Pipe'}</li>
                    <li><strong>Data Rows:</strong> {csvInput.split('\n').length - (hasHeader ? 1 : 0)} rows</li>
                  </ul>
                </div>
                
                <div className="mt-4">
                  <button
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    onClick={convertCsvToExcel}
                  >
                    Convert Again
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About CSV to Excel Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms CSV (Comma-Separated Values) data into Excel spreadsheet formats. CSV files are simple text files containing tabular data with values separated by a delimiter, typically a comma. The converter parses this data and generates a properly formatted Excel file that preserves data types, formatting, and structure.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Multiple Excel Formats</strong> - Support for XLSX (Excel 2007+), XLS (Excel 97-2004), and ODS (OpenDocument)</li>
              <li><strong>Customizable Worksheet</strong> - Set your own worksheet name</li>
              <li><strong>File Upload</strong> - Upload CSV files directly or paste content manually</li>
              <li><strong>Delimiter Support</strong> - Handle various delimiters (comma, semicolon, tab, pipe)</li>
              <li><strong>Custom Headers</strong> - Use first row as headers with formatting</li>
              <li><strong>Data Type Detection</strong> - Automatically converts values to appropriate Excel types</li>
              <li><strong>Date Handling</strong> - Properly formats and recognizes date values</li>
              <li><strong>Value Trimming</strong> - Option to trim whitespace from field values</li>
              <li><strong>Empty Line Handling</strong> - Skip or include empty lines as needed</li>
              <li><strong>Quoted Value Support</strong> - Properly handles quoted fields in CSV</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">CSV Format Best Practices</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use consistent delimiter throughout the file</li>
              <li>Include a header row for better column naming in Excel</li>
              <li>Use double quotes around values that contain delimiters or newlines</li>
              <li>Escape double quotes within quoted fields by doubling them (e.g., "He said ""Hello"" to me")</li>
              <li>Keep column count consistent across all rows</li>
              <li>Avoid commas in values unless properly quoted</li>
              <li>Use ISO date formats (YYYY-MM-DD) for better date recognition</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Excel File Format Differences</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>XLSX</strong> - Modern Excel format with better compatibility and features, smaller file size</li>
              <li><strong>XLS</strong> - Legacy Excel format for compatibility with older Excel versions</li>
              <li><strong>ODS</strong> - OpenDocument format for compatibility with LibreOffice and other open-source applications</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Ensure your CSV data is well-formed with consistent columns</li>
              <li>Use headers to provide meaningful column names in the Excel file</li>
              <li>Choose the appropriate delimiter based on your data</li>
              <li>Enable dynamic typing for proper data type conversion</li>
              <li>For date columns, use consistent date formatting throughout</li>
              <li>For very large files, be patient as processing may take longer</li>
              <li>Select the Excel format based on your compatibility needs</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvToExcel;